import { useState, useRef, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Tick02Icon, Cancel01Icon } from '@hugeicons/core-free-icons';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * InlineEdit — Double-click to edit a field inline.
 *
 * @param {object} props
 * @param {string} props.value - Current display value
 * @param {(newValue: string) => Promise<void>} props.onSave - Async save handler
 * @param {string} [props.type] - Input type: 'text' | 'number' | 'date'
 * @param {string} [props.placeholder] - Placeholder when editing
 * @param {string} [props.className] - Additional classes for display mode
 * @param {React.ReactNode} [props.children] - Custom display content (overrides value text)
 */
export function InlineEdit({ value, onSave, type = 'text', placeholder, className, children }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  const startEdit = () => {
    setDraft(value || '');
    setEditing(true);
  };

  const cancel = () => {
    setDraft(value || '');
    setEditing(false);
  };

  const save = async () => {
    if (draft === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      // Keep editing on error
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') cancel();
  };

  if (!editing) {
    return (
      <div
        onDoubleClick={startEdit}
        className={cn('cursor-default group', className)}
        title="Doble clic para editar"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') startEdit();
        }}
        aria-label={`Editar: ${value || 'vacío'}`}>
        {children || (
          <span className="group-hover:bg-primary/5 group-hover:rounded px-0.5 -mx-0.5 transition-colors">
            {value || '—'}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Input
        ref={inputRef}
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={(e) => {
          if (!e.relatedTarget?.closest('[data-inline-action]')) save();
        }}
        placeholder={placeholder}
        disabled={saving}
        className={cn('h-7 text-sm px-2 min-w-0', type === 'date' && 'min-w-[8rem]')}
      />
      <button
        data-inline-action
        onClick={save}
        disabled={saving}
        className="h-6 w-6 rounded flex items-center justify-center text-accent hover:bg-accent/10 shrink-0"
        aria-label="Guardar">
        <HugeiconsIcon icon={Tick02Icon} className="h-3.5 w-3.5" />
      </button>
      {value && (
        <button
          data-inline-action
          onClick={async () => {
            setDraft('');
            setSaving(true);
            try {
              await onSave('');
              setEditing(false);
            } catch {
            } finally {
              setSaving(false);
            }
          }}
          disabled={saving}
          className="h-7 px-2 rounded text-xs text-destructive hover:bg-destructive/10 shrink-0"
          aria-label="Limpiar valor">
          Limpiar
        </button>
      )}
      <button
        data-inline-action
        onClick={cancel}
        className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:bg-muted shrink-0"
        aria-label="Cancelar">
        <HugeiconsIcon icon={Cancel01Icon} className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
